import React, { useMemo, useCallback } from "react";
import { Avatar, Typography, Table, Badge, Tooltip, Popover } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import Moment from "moment";
import { extendMoment } from "moment-range";
import Patient from "../../../components/tables/Patient";
import Date from "../../../components/tables/Date";
import tinycolor from "tinycolor2";

const moment = extendMoment(Moment);
const { Text } = Typography;

const columns = (intl) => [
  {
    title: intl.formatMessage({ id: "app.plans.eventConflictsPatientName" }),
    dataIndex: "patient",
    render: (text, record) => <Patient record={record.order.patient} />,
    width: "220px",
  },
  {
    title: intl.formatMessage({ id: "app.plans.eventConflictsDate" }),
    dataIndex: "date",
    render: (text) => <Date text={text} format={"L"} />,
    width: "140px",
  },
  {
    title: intl.formatMessage({ id: "app.plans.dayInWeek" }),
    dataIndex: "date",
    render: (text, record) => (
      <Text>{moment.weekdays(moment(text).weekday())}</Text>
    ),
    width: "80px",
  },
  {
    title: intl.formatMessage({ id: "app.plans.eventConflictsStart" }),
    dataIndex: "date",
    render: (text) => <Date text={text} format={"HH:mm"} />,
    width: "140px",
  },
  {
    title: intl.formatMessage({ id: "app.plans.eventConflictsEnd" }),
    dataIndex: "date",
    render: (text, record) => (
      <Date
        text={moment(text).add(record.duration_minutes, "minutes")}
        format={"HH:mm"}
      />
    ),
    width: "140px",
  },
];
const CustomEvent = (props) => {
  const { event, ordersPalette } = props;
  const conflict_events = useSelector(
    (state) => state.plan.activeSimulatedPlan.conflict_events || []
  );
  const non_available_employee_events = useSelector(
    (state) =>
      state.plan.activeSimulatedPlan.non_available_employee_events || []
  );

  const conflicts = conflict_events.filter(
    (item) =>
      item.conflict_weekday === event.weekday &&
      item.conflict_start_time_minutes === event.start_time_minutes &&
      item.employee_id === event.employee_id
  );

  const intl = useIntl();
  const startsInCurrentMonth =
    event.order?.effective_from &&
    moment(event.order.effective_from) >= moment().startOf("month");
  const endsInCurrentMonth =
    event.order?.effective_to &&
    moment(event.order.effective_to) <= moment().endOf("month");
  const categoryName = event.order?.client?.category?.name || "";
  const letter = event.employee ? event.employee.first_name.charAt(0) : "";

  const color = useMemo(() => {
    let baseColor = categoryName
      ? ordersPalette[categoryName] || "#C31A57"
      : "#ffffff";
    if (startsInCurrentMonth && endsInCurrentMonth) {
      return tinycolor(baseColor).darken(15).desaturate(10).toHexString(); // "#f53d3d"
    } else if (endsInCurrentMonth) {
      return tinycolor(baseColor).darken(30).desaturate(10).toHexString(); // "#f53d3d"
    }
    return baseColor;
  }, [startsInCurrentMonth, endsInCurrentMonth, categoryName, ordersPalette]);

  const fullName = event.employee
    ? `${event.employee.surename} ${event.employee.first_name}`
    : "";

  const tableColumns = useMemo(() => columns(intl), [intl]);

  const conflictsContent = useMemo(() => {
    return (
      conflicts &&
      conflicts.length > 0 && (
        <div style={{ width: "800px" }}>
          <Table
            bordered
            dataSource={conflicts}
            columns={tableColumns}
            pagination={false}
            scroll={{ y: window.innerHeight / 3 }}
          />
        </div>
      )
    );
  }, [conflicts]);

  moment.relativeTimeRounding((t) => {
    const DIGITS = 2; // like: 2.56 minutes
    return Math.round(t * Math.pow(10, DIGITS)) / Math.pow(10, DIGITS);
  });
  moment.relativeTimeThreshold("y", 365);
  moment.relativeTimeThreshold("M", 12);
  moment.relativeTimeThreshold("w", 4);
  moment.relativeTimeThreshold("d", 31);
  moment.relativeTimeThreshold("h", 24);
  moment.relativeTimeThreshold("m", 60);
  moment.relativeTimeThreshold("s", 60);
  moment.relativeTimeThreshold("ss", 0);
  const diff = moment
    .duration(moment(event.end).diff(moment(event.start)))
    .asSeconds();

  const humanizeTimeFrame = moment.duration(diff, "s").humanize();

  const AdditionalInfo = useCallback(() => {
    if (event.nonAvailable) {
      return (
        <Tooltip
          title={intl.formatMessage({
            id: "app.scheduling.nonAvailabilityWarning",
          })}
          color={"red"}
          key={"warning"}
        >
          <WarningOutlined style={{ padding: "0 5px", color: "red" }} />
        </Tooltip>
      );
    } else if (!event.employee) {
      return (
        <Tooltip
          title={intl.formatMessage({
            id: "app.scheduling.noScheduledWarning",
          })}
          color={"red"}
          key={"warning"}
        >
          <WarningOutlined style={{ padding: "0 5px", color: "red" }} />
        </Tooltip>
      );
    } else if (non_available_employee_events.includes(event.id)) {
      return (
        <Tooltip
          title={intl.formatMessage({
            id: "app.plans.employeeNotAvailable",
          })}
          color={"red"}
          key={"warning"}
        >
          <WarningOutlined style={{ padding: "0 5px", color: "red" }} />
        </Tooltip>
      );
    }
    return null;
  }, [event, intl, non_available_employee_events]);

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {!event.employee ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Text style={{ color: color, fontSize: "12px" }}>
            {`${moment(event.start).format("HH:mm")} - ${moment(
              event.end
            ).format("HH:mm")}`}
          </Text>
          {event.is_day_center && (
            <Text style={{ color: "initial", textAlign: "start" }}>
              {intl.formatMessage({
                id: "app.scheduling.day_center",
              })}
            </Text>
          )}
        </div>
      ) : (
        <Tooltip title={humanizeTimeFrame} key={"range"}>
          <div style={{ display: "flex", alignItems: "center" }}>
            {event.employee.avatar_url ? (
              <Avatar src={event.employee.avatar_url} />
            ) : (
              <Avatar
                style={{
                  color: "#000000",
                  backgroundColor: "#ffffff",
                  border: "1px solid #838383",
                }}
              >
                {letter}
              </Avatar>
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                padding: "0 5px",
              }}
            >
              <Text
                style={{
                  color: color,
                  textAlign: "start",
                  fontSize: "12px",
                  fontWeight: "700",
                }}
              >
                {`${moment(event.start).format("HH:mm")} - ${moment(
                  event.end
                ).format("HH:mm")}`}
              </Text>
              <Text
                ellipsis={true}
                style={{ color: "initial", textAlign: "start" }}
              >
                {fullName}
              </Text>
              {event.duration_minutes > 30 && conflicts.length > 0 && (
                <Popover
                  content={conflictsContent}
                  title={
                    <Badge
                      style={{ padding: "8px 0" }}
                      status="error"
                      text={intl.formatMessage({
                        id: "app.plans.eventConflictsTitle",
                      })}
                    />
                  }
                >
                  <WarningOutlined style={{ padding: "0 5px", color: "red" }} />
                </Popover>
              )}
              <AdditionalInfo />
            </div>
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default CustomEvent;
